import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer } from "src/components/organisms/contents";

/** 체크가 나타났다 사라지는 애니메이션 아이콘 */
export const AnimatedCheck: React.FC<{
  size?: number;
  showMs?: number; // 나타나 있는 시간 (체크가 그려진 뒤 유지)
  onEnd?: () => void; // 사라진 뒤 콜백
}> = ({ size = 140, showMs = 800, onEnd }) => {
  const [phase, setPhase] = useState<"draw" | "hold" | "fade">("draw");

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 700); // 원+체크 그리기 완료
    const t2 = setTimeout(() => setPhase("fade"), 700 + showMs);
    const t3 = setTimeout(() => onEnd?.(), 700 + showMs + 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [showMs, onEnd]);

  const s = size;
  const stroke = 6;

  // ✅ 실제 외곽선 원의 반지름과 둘레 정확 계산
  const rOutline = (s - stroke * 2) / 2;
  const circleLen = 2 * Math.PI * rOutline;

  // 체크 길이는 대략치로 충분
  const checkLen = 120;

  return (
    <div
      style={{
        width: s,
        height: s,
        display: "grid",
        placeItems: "center",
        filter: "drop-shadow(0 6px 16px rgba(249,115,22,.25))",
        // 스케일 애니메이션은 래퍼에서만 수행 (중복 스케일로 인한 드리프트 방지)
        transform:
          phase === "draw"
            ? "scale(.9)"
            : phase === "hold"
            ? "scale(1)"
            : "scale(.98)",
        opacity: phase === "fade" ? 0 : 1,
        transition: "transform .25s ease, opacity .4s ease",
        transformOrigin: "center",
        willChange: "transform, opacity",
        backfaceVisibility: "hidden",
      }}
    >
      {/* keyframes 주입 */}
      <style>{`
        @keyframes dashCircle { from { stroke-dashoffset: ${circleLen}; } to { stroke-dashoffset: 0; } }
        @keyframes dashCheck  { from { stroke-dashoffset: ${checkLen}; }  to { stroke-dashoffset: 0; } }
      `}</style>

      <svg
        width={s}
        height={s}
        viewBox={`0 0 ${s} ${s}`}
        style={{
          display: "block", // 인라인 베이스라인 영향 제거
          transformOrigin: "center",
          transformBox: "fill-box",
          shapeRendering: "geometricPrecision",
        }}
      >
        {/* 배경 원 (연한 노랑) */}
        <circle cx={s / 2} cy={s / 2} r={(s - stroke) / 2} fill="#FFF4D8" />

        {/* 외곽선 그려지기 */}
        <circle
          cx={s / 2}
          cy={s / 2}
          r={rOutline}
          fill="none"
          stroke="#FFC451"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circleLen}
          vectorEffect="non-scaling-stroke" // ✅ 스케일 시 스트로크 두께 고정
          style={{ animation: `dashCircle .6s ease forwards` }}
        />

        {/* 체크 그려지기 */}
        <path
          d={`M ${s * 0.35} ${s * 0.52} L ${s * 0.48} ${s * 0.64} L ${
            s * 0.7
          } ${s * 0.4}`}
          fill="none"
          stroke="#F97316"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={checkLen}
          vectorEffect="non-scaling-stroke" // ✅
          style={{ animation: `dashCheck .5s ease .15s forwards` }}
        />
      </svg>
    </div>
  );
};

/** 완료 페이지 예시 */
export default function FinishPage() {
  const px = (n: number) => n; // 프로젝트 px 헬퍼 대체 가능
  const navigate = useNavigate();
  const { reg } = useParams();
  // const [hideIcon, setHideIcon] = useState(false);
  const [isReady, setIsReady] = useState(false); // 버튼 활성화 여부

  return (
    <PageContainer isNavBar={false} active={null}>
      <>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            paddingTop: 200,
            paddingBottom: 12,
          }}
        >
          <AnimatedCheck
            size={150}
            showMs={800}
            onEnd={() => setIsReady(true)}
          />
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#111827",
                lineHeight: 1.35,
              }}
            >
              모든 단계를
              <br />
              성공적으로 완료했어요!
            </div>
            <div style={{ fontSize: 15, marginTop: 8, color: "#6B7280" }}>
              이제 루틴케어로 건강 습관 챙길 준비 끝~!
            </div>
          </div>
        </div>
        {/* 본문 끝 안전 여백: 고정 버튼과 겹침 방지 */}
        <div style={{ height: px(96) }} />
        {/* ✅ 하단 고정 버튼: 레이아웃 변경 없이 버튼만 고정 */}
        <div
          style={{
            width: "100%",
            maxWidth: "45rem",
            position: "fixed",
            left: "50%",
            bottom: 0,
            transform: "translateX(-50%)",
            padding: `${px(12)}px ${px(16)}px calc(${px(
              12
            )}px + env(safe-area-inset-bottom))`,
            background: "rgba(243,244,246,0.9)",
            backdropFilter: "blur(6px)",
            boxShadow: "0 -6px 14px rgba(0,0,0,0.06)",
          }}
        >
          <button
            style={{
              width: "100%",
              height: px(52),
              borderRadius: px(12),
              border: "none",
              background: !isReady
                ? "#FFD9B3"
                : "linear-gradient(180deg,#FFA62E,#F97316)",
              color: "#fff",
              fontWeight: 700,
              fontSize: px(16),
              boxShadow: "0 8px 18px rgba(249,115,22,.32)",
              cursor: "pointer",
            }}
            disabled={!isReady}
            onClick={() => {
              if (reg === "join") {
                navigate("/auth/login");
              } else {
                navigate("/");
              }
            }}
          >
            확인
          </button>
        </div>
      </>
    </PageContainer>
  );
}
